import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Autocomplete, Button, CircularProgress, TextField } from '@mui/material';
import { useMemo } from 'react';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import type { input } from 'zod';

import type { DialogType } from '../../../@types';
import type { AwardGgaFreeSpinsBatchRequest, GgaFreeSpinGameOption } from '../../../@types/casino';
import { QUERY_KEYS } from '../../../constants';
import { useToast } from '../../../contexts/ToastContext';
import { useToastErrorMessage } from '../../../hooks/useToastErrorMessage';
import { useAwardGgaFreeSpinsBatch, useGgaFreeSpinOptions } from '../../../queries/casino';
import useGgaFreeSpinBetSelection from '../../../hooks/useGgaFreeSpinBetSelection';
import { ggaFreeSpinsSettingsSchema, type GgaFreeSpinsSettingsFormData } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';
import CustomerSelect from '../../molecules/CustomerSelect';
import FormNumberInput from '../../molecules/FormNumberInput';
import GgaBetLevelField from '../../molecules/GgaBetLevelField';

const DEFAULT_FORM_VALUES: GgaFreeSpinsSettingsFormData = {
  providerId: '',
  gameId: '',
  playerIds: [],
  freespinAmount: null,
  betAmount: null,
  message: '',
};

type ProviderOption = {
  id: string;
  name: string;
};

const GgaFreeSpinsSettingsForm = ({ closeModal }: DialogType) => {
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const gameOptionsQuery = useGgaFreeSpinOptions();
  const awardFreeSpins = useAwardGgaFreeSpinsBatch();
  const gameOptions = useMemo(() => gameOptionsQuery.data ?? [], [gameOptionsQuery.data]);
  const providerOptions = useMemo<ProviderOption[]>(() => {
    const providers = new Map<string, ProviderOption>();

    gameOptions.forEach((game) => {
      const id = String(game.providerId);
      providers.set(id, { id, name: game.providerName ?? `Provider ${id}` });
    });

    return Array.from(providers.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [gameOptions]);

  const form = useForm<input<typeof ggaFreeSpinsSettingsSchema>, unknown, GgaFreeSpinsSettingsFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(ggaFreeSpinsSettingsSchema, undefined, { mode: 'sync' }),
    mode: 'onChange',
    disabled: awardFreeSpins.isPending,
  });
  const [providerId, gameId, betAmount] = useWatch({
    control: form.control,
    name: ['providerId', 'gameId', 'betAmount'],
  });
  const { isValid } = useFormState({ control: form.control });
  const filteredGames = useMemo(
    () => gameOptions.filter((game) => String(game.providerId) === providerId),
    [gameOptions, providerId]
  );

  const { isLevelGame, betLevels, invalidLevel } = useGgaFreeSpinBetSelection(
    filteredGames.find((game) => game.id === gameId),
    betAmount
  );

  const onSubmit = (data: GgaFreeSpinsSettingsFormData) => {
    if (invalidLevel) return;
    const payload: AwardGgaFreeSpinsBatchRequest = {
      playerIds: data.playerIds,
      gameId: Number(data.gameId),
      freespinAmount: data.freespinAmount as number,
      betAmount: data.betAmount as number,
      message: data.message,
    };

    awardFreeSpins.mutate(payload, {
      onSuccess: (response) => {
        enqueueSnackbar({ message: response.message || 'Free spins awarded successfully.', variant: 'success' });
        closeModal?.();
      },
      onError: handleErrorMessage,
    });
  };

  const optionsUnavailable = gameOptionsQuery.isError || (!gameOptionsQuery.isLoading && gameOptions.length === 0);
  const disableSubmit =
    optionsUnavailable || gameOptionsQuery.isLoading || awardFreeSpins.isPending || !isValid || invalidLevel;

  return (
    <FormModalLayout
      label='Free Spins Settings'
      titleAlign='center'
      onSave={form.handleSubmit(onSubmit)}
      onClose={closeModal}
      actionButtonLabel='Add'
      showEditTabs={false}
      disableSubmit={disableSubmit}
    >
      {gameOptionsQuery.isError && (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={() => gameOptionsQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load GGA free-spin providers and games.
        </Alert>
      )}

      {!gameOptionsQuery.isLoading && !gameOptionsQuery.isError && gameOptions.length === 0 && (
        <Alert severity='warning'>GGA did not return any games that support free spins.</Alert>
      )}

      <Controller
        name='providerId'
        control={form.control}
        render={({ field, fieldState }) => {
          const selectedProvider = providerOptions.find((provider) => provider.id === field.value) ?? null;

          return (
            <Autocomplete
              options={providerOptions}
              value={selectedProvider}
              onChange={(_event, provider) => {
                field.onChange(provider?.id ?? '');
                form.setValue('gameId', '', { shouldDirty: true, shouldValidate: true });
                form.setValue('betAmount', null, { shouldValidate: true });
              }}
              onBlur={field.onBlur}
              getOptionLabel={(provider) => provider.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={gameOptionsQuery.isLoading}
              disabled={gameOptionsQuery.isError}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Provider'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {gameOptionsQuery.isLoading && <CircularProgress color='inherit' size={20} />}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          );
        }}
      />

      <Controller
        name='gameId'
        control={form.control}
        render={({ field, fieldState }) => {
          const selectedGame = filteredGames.find((game) => game.id === field.value) ?? null;

          return (
            <Autocomplete<GgaFreeSpinGameOption>
              options={filteredGames}
              value={selectedGame}
              onChange={(_event, game) => {
                field.onChange(game?.id ?? '');
                form.setValue('betAmount', null, { shouldValidate: true });
              }}
              onBlur={field.onBlur}
              getOptionLabel={(game) => game.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!providerId || gameOptionsQuery.isError}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Game'
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          );
        }}
      />

      <CustomerSelect
        control={form.control}
        name='playerIds'
        label='Players'
        queryKey={[QUERY_KEYS.customers, 'gga-free-spins-active-players']}
        queryParams={{ filters: { isActive: true } }}
        required
        error={form.formState.errors.playerIds}
        remoteErrorText='Unable to load active players.'
      />

      <FormNumberInput name='freespinAmount' control={form.control} label='Free Spin Quantity' required fullWidth />
      {isLevelGame ? (
        <Controller
          name='betAmount'
          control={form.control}
          render={({ field, fieldState }) => (
            <GgaBetLevelField
              betLevels={betLevels}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={awardFreeSpins.isPending}
              error={fieldState.error?.message}
            />
          )}
        />
      ) : (
        <FormNumberInput
          name='betAmount'
          control={form.control}
          label='Free Spin Bet Amount'
          allowDecimals
          decimalPlaces={2}
          valueAsString
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          required
          fullWidth
        />
      )}

      <Controller
        name='message'
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label='Message'
            required
            multiline
            minRows={3}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        )}
      />
    </FormModalLayout>
  );
};

export default GgaFreeSpinsSettingsForm;
